import React, { useEffect, useState } from "react";
import { PieChart, Pie, Cell } from "recharts";
import { supabase } from "@/integrations/supabase/client";
import { useSupabaseAuth } from "@/context/SupabaseAuthContext";
import DailyLog from "./DailyLog";
import {
  ChevronLeft,
  ChevronRight,
  Utensils,
  Car,
  ShoppingBag,
  Zap,
  Calendar,
} from "lucide-react";

const CATEGORIES = [
  { key: "food", label: "Food & Drink", icon: Utensils, color: "#10b981" },
  { key: "transport", label: "Travel", icon: Car, color: "#f59e0b" },
  {
    key: "goods",
    label: "Goods & Services",
    icon: ShoppingBag,
    color: "#8b5cf6",
  },
  { key: "utilities", label: "Utilities", icon: Zap, color: "#ef4444" },
];

function getMonthString(date) {
  return date.toLocaleString("default", { month: "long", year: "numeric" });
}

function getMonthRange(date) {
  const start = new Date(date.getFullYear(), date.getMonth(), 1);
  const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  return [start.toISOString().slice(0, 10), end.toISOString().slice(0, 10)];
}

interface CarbonSummaryWidgetProps {
  onGetStarted: () => void;
  onCardClick?: (category: string) => void;
  month: Date;
  setMonth: (month: Date) => void;
  refreshTrigger?: number;
  onDataSaved?: () => void;
}

const CarbonSummaryWidget: React.FC<CarbonSummaryWidgetProps> = ({
  onGetStarted,
  onCardClick,
  month,
  setMonth,
  refreshTrigger,
  onDataSaved,
}) => {
  const { user } = useSupabaseAuth();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showEditLog, setShowEditLog] = useState(false);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    const [start, end] = getMonthRange(month);

    (supabase.from("carbon_logs").select("*") as any)
      .eq("user_id", user.id)
      .gte("date", start)
      .lte("date", end)
      .then(({ data: logs }) => {
        // Aggregate by category
        let food = 0,
          transport = 0,
          goods = 0,
          utilities = 0;
        let total = 0;

        (logs || []).forEach((log) => {
          food += log.food
            ? log.food.reduce(
                (sum, f) =>
                  sum +
                  (parseFloat(f.servings) || 0) *
                    (f.type === "meat"
                      ? 5
                      : f.type === "vegetarian"
                        ? 2
                        : f.type === "vegan"
                          ? 1.5
                          : 1),
                0,
              )
            : 0;
          transport += log.transport
            ? log.transport.reduce(
                (sum, t) =>
                  sum +
                  (parseFloat(t.distance) || 0) *
                    (t.mode === "car"
                      ? 0.21
                      : t.mode === "bus"
                        ? 0.09
                        : t.mode === "flight"
                          ? 0.255
                          : 0),
                0,
              )
            : 0;
          utilities += log.home
            ? (parseFloat(log.home.electricity) || 0) * 0.4 +
              (parseFloat(log.home.gas) || 0) * 0.2 +
              (parseFloat(log.home.water) || 0) * 0.0003
            : 0;

          if (log.goods != null && log.goods !== "") {
            const goodsValue = parseFloat(log.goods);
            goods += (!isNaN(goodsValue) ? goodsValue : 0) * 0.5;
          }
          total += log.total_co2 || 0;
        });

        const breakdown = [
          {
            key: "food",
            value: Math.round(food),
            percent: total ? Math.round((food / total) * 100) : 0,
          },
          {
            key: "transport",
            value: Math.round(transport),
            percent: total ? Math.round((transport / total) * 100) : 0,
          },
          {
            key: "goods",
            value: Math.round(goods),
            percent: total ? Math.round((goods / total) * 100) : 0,
          },
          {
            key: "utilities",
            value: Math.round(utilities),
            percent: total ? Math.round((utilities / total) * 100) : 0,
          },
        ];

        setData({ total: Math.round(total), breakdown });
        setLoading(false);
      });
  }, [user, month, refreshTrigger]);

  const handlePrevMonth = () => {
    setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1));
  };

  if (!user) return null;
  if (loading)
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-sm text-muted-foreground">Loading summary...</div>
      </div>
    );

  if (!data || data.total === 0) {
    const hasAnyData = data && data.breakdown.some((entry) => entry.value > 0);
    if (!hasAnyData) {
      return (
        <div className="text-center space-y-4">
          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-foreground">
              Your Carbon Footprint
            </h3>
            <p className="text-sm text-muted-foreground">
              No data yet for this month.
            </p>
          </div>
          <button
            className="bg-[#40e0d0] hover:bg-[#40e0d0]/90 text-black px-6 py-2 rounded-md font-medium transition-colors"
            onClick={onGetStarted}
          >
            Start Tracking
          </button>
        </div>
      );
    }
  }

  return (
    <div className="space-y-6">
      {/* Month Navigation */}
      <div className="flex items-center justify-center">
        <div className="flex items-center gap-3 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
          <button
            onClick={handlePrevMonth}
            className="p-2 hover:bg-white dark:hover:bg-gray-700 rounded-md transition-colors"
            title="Previous Month"
          >
            <ChevronLeft size={16} />
          </button>

          <div className="flex items-center gap-2 px-3 py-1 min-w-[140px] justify-center">
            <Calendar size={16} className="text-muted-foreground" />
            <span className="text-sm font-medium">{getMonthString(month)}</span>
          </div>

          <button
            onClick={handleNextMonth}
            className="p-2 hover:bg-white dark:hover:bg-gray-700 rounded-md transition-colors"
            title="Next Month"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* Total and Chart */}
      <div className="flex flex-col items-center space-y-4">
        <div className="text-center space-y-1">
          <div className="text-3xl font-bold text-foreground">{data.total}</div>
          <div className="text-sm text-muted-foreground">
            kg CO₂e this month
          </div>
        </div>

        <div className="relative">
          <PieChart width={120} height={120}>
            <Pie
              data={data.breakdown}
              dataKey="value"
              cx="50%"
              cy="50%"
              innerRadius={35}
              outerRadius={55}
              startAngle={90}
              endAngle={-270}
              stroke="none"
            >
              {data.breakdown.map((entry, idx) => (
                <Cell key={entry.key} fill={CATEGORIES[idx].color} />
              ))}
            </Pie>
          </PieChart>
        </div>
      </div>

      {/* Category Breakdown */}
      <div className="space-y-2">
        {data.breakdown.map((entry, idx) => {
          const IconComponent = CATEGORIES[idx].icon;
          return (
            <button
              key={entry.key}
              className="w-full flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors border-l-4"
              style={{ borderLeftColor: CATEGORIES[idx].color }}
              onClick={() => onCardClick && onCardClick(entry.key)}
            >
              <div className="flex items-center gap-3">
                <IconComponent
                  size={16}
                  style={{ color: CATEGORIES[idx].color }}
                />
                <div className="text-left">
                  <div className="text-sm font-medium text-foreground">
                    {CATEGORIES[idx].label}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {entry.percent}% of total
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm font-semibold text-foreground">
                  {entry.value} kg
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Edit Log Modal */}
      {showEditLog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto relative">
            <button
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 z-10"
              onClick={() => setShowEditLog(false)}
              aria-label="Close edit log modal"
              title="Close"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <div className="p-6">
              <DailyLog
                onClose={() => setShowEditLog(false)}
                onDataSaved={onDataSaved}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CarbonSummaryWidget;
